import {
  type DrillDashboardPlan,
  type DrillKpis,
  type DrillPlanFilter,
  drillKpis,
  drillPlanMatches,
} from '@felix/domain';
import type { DrillPlan, DrillVessel } from './plan-types.js';

/**
 * Tatbikat Planner ve panosunun saf görünüm yardımcıları (ADR 0128 Faz 2 Ek): yıllık ızgara,
 * aylık liste, takvim noktaları, dönem ve filo karşılaştırması. Süzgeç ve KPI `@felix/domain`
 * `drills/dashboard.ts`'den (gemi ve ofis aynı hesap).
 */

export type DrillMark = 'done' | 'due' | 'overdue' | 'postponed';

export function dashboardPlan(plan: DrillPlan): DrillDashboardPlan {
  return {
    vesselId: plan.vesselId,
    templateId: plan.templateId,
    categoryId: plan.categoryId,
    responsibleUserId: plan.responsibleUserId,
    state: plan.state,
    status: plan.status,
    nextDueOn: plan.due.nextDueOn,
    originalDueOn: plan.due.originalDueOn,
    conducts: plan.conducts.filter((c) => c.status === 'approved').map((c) => c.conductedOn),
    cycles: plan.cycles,
  };
}

export function filterPlans(plans: readonly DrillPlan[], filter: DrillPlanFilter): DrillPlan[] {
  return plans.filter((plan) => drillPlanMatches(dashboardPlan(plan), filter));
}

export function kpisOf(
  plans: readonly DrillPlan[],
  range: { from: string; to: string; today: string },
): DrillKpis {
  return drillKpis(plans.map(dashboardPlan), range);
}

export interface DrillDayItem {
  plan: DrillPlan;
  date: string;
  mark: DrillMark;
}

/**
 * Planın tarihli olayları: onaylı uygulama (`done`), yürürlükteki vade (`due`, geçtiyse
 * `overdue`; ertelenmişse `postponed`), sonraki döngü vadeleri (bir yıl ileriye kadar `due`) ve
 * yapılmamış (geç ya da hiç) döngülerin vadesi (`overdue`). Taslak ve iptal planın vadesi yok.
 */
export function planEvents(plan: DrillPlan, today: string): DrillDayItem[] {
  const out: DrillDayItem[] = plan.conducts
    .filter((c) => c.status === 'approved')
    .map((c) => ({ plan, date: c.conductedOn, mark: 'done' as const }));
  if (plan.state !== 'active') return out;
  for (const cycle of plan.cycles) {
    if (cycle.result === 'missed' || cycle.result === 'late')
      out.push({ plan, date: cycle.dueOn, mark: 'overdue' });
  }
  const next = plan.due.nextDueOn;
  out.push({
    plan,
    date: next,
    mark: next < today ? 'overdue' : plan.due.basis === 'postponed' ? 'postponed' : 'due',
  });
  // Sonraki döngüler: yürürlükteki vadeden aralıkla, bir yıl ileriye kadar
  const limit = addMonths(today, 12);
  let cursor = addMonths(next, plan.intervalMonths);
  for (let i = 0; i < 24 && cursor <= limit; i += 1) {
    out.push({ plan, date: cursor, mark: 'due' });
    cursor = addMonths(cursor, plan.intervalMonths);
  }
  return out;
}

/** Takvim ayı ekleme; ay sonu kırpılır (kural motoruyla aynı). */
export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const total = (y ?? 2000) * 12 + ((m ?? 1) - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
    Math.min(d ?? 1, last),
  ).padStart(2, '0')}`;
}

/** Ayın olayları (gün, sonra kod sırasıyla). `month`: `YYYY-MM`. */
export function monthItems(
  plans: readonly DrillPlan[],
  month: string,
  today: string,
): DrillDayItem[] {
  return plans
    .flatMap((plan) => planEvents(plan, today))
    .filter((item) => item.date.startsWith(`${month}-`))
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || a.plan.templateCode.localeCompare(b.plan.templateCode),
    );
}

const MARK_ORDER: Record<DrillMark, number> = { overdue: 0, postponed: 1, due: 2, done: 3 };

/** Takvim noktaları: günün işaretleri (tekil, önem sırasıyla). */
export function calendarMarks(
  plans: readonly DrillPlan[],
  month: string,
  today: string,
): Map<string, DrillMark[]> {
  const map = new Map<string, DrillMark[]>();
  for (const item of monthItems(plans, month, today)) {
    const list = map.get(item.date) ?? [];
    if (!list.includes(item.mark)) list.push(item.mark);
    map.set(item.date, list);
  }
  for (const list of map.values()) list.sort((a, b) => MARK_ORDER[a] - MARK_ORDER[b]);
  return map;
}

/** Yıllık ızgara: planın her ayındaki en önemli işaret (yoksa `null`). */
export function annualRow(plan: DrillPlan, year: number, today: string): (DrillMark | null)[] {
  const events = planEvents(plan, today);
  return Array.from({ length: 12 }, (_, i) => {
    const prefix = `${year}-${String(i + 1).padStart(2, '0')}-`;
    const marks = events.filter((e) => e.date.startsWith(prefix)).map((e) => e.mark);
    if (marks.length === 0) return null;
    return [...marks].sort((a, b) => MARK_ORDER[a] - MARK_ORDER[b])[0] ?? null;
  });
}

export type DrillPeriod = 'month' | 'quarter' | 'year';

/** Dönemin ilk ve son günü (bugünü içeren ay, çeyrek ya da yıl). */
export function periodRange(period: DrillPeriod, today: string): { from: string; to: string } {
  const [y, m] = today.split('-').map(Number);
  const year = y ?? 2000;
  const month = m ?? 1;
  const startMonth =
    period === 'year' ? 1 : period === 'quarter' ? month - ((month - 1) % 3) : month;
  const length = period === 'year' ? 12 : period === 'quarter' ? 3 : 1;
  const from = `${year}-${String(startMonth).padStart(2, '0')}-01`;
  const to = addMonths(from, length);
  const last = new Date(Date.parse(`${to}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  return { from, to: last };
}

export interface FleetRow {
  vessel: DrillVessel;
  kpis: DrillKpis;
}

/** Ofisin filo karşılaştırması: geminin kendi bugünüyle KPI. */
export function fleetComparison(
  plans: readonly DrillPlan[],
  vessels: readonly DrillVessel[],
  range: { from: string; to: string },
): FleetRow[] {
  return vessels.map((vessel) => ({
    vessel,
    kpis: kpisOf(
      plans.filter((p) => p.vesselId === vessel.id),
      { ...range, today: vessel.today },
    ),
  }));
}

/** Bugün: tek gemide geminin günü, filoda en geç gemi günü (yoksa UTC). */
export function todayOf(vessels: readonly DrillVessel[], now: Date = new Date()): string {
  const days = vessels.map((v) => v.today).sort();
  return days.at(-1) ?? now.toISOString().slice(0, 10);
}
